// Design-system tokens shared by both appearances.
// Everything here lands on `:root` through GlobalStyles (see globalStyles.ts),
// so portals (Menu, Popover, Dialog, toasts), the left nav, the search modal
// and the Assistant window all read the same values as the page.
// Light values are the original glass look; dark values are "dark glass".
// See DESIGN_SYSTEM.md for the semantics of each token.

export type AppearanceMode = 'light' | 'dark'

// Mirrored in public/index.html (pre-React background + color-scheme).
export const PAGE_BG: Record<AppearanceMode, string> = {
  light: '#F7F8FB',
  dark: '#0F151C',
}

const NOISE =
  'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27 viewBox=%270 0 140 140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.72%27 numOctaves=%273%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27140%27 height=%27140%27 filter=%27url(%23n)%27 opacity=%270.18%27/%3E%3C/svg%3E")'

const pick =
  (mode: AppearanceMode) =>
  (light: string, dark: string): string =>
    mode === 'light' ? light : dark

export const getLeftNavCssVars = (mode: AppearanceMode) => {
  const v = pick(mode)

  return {
    '--left-nav-surface': v(
      'rgba(255, 255, 255, 0.72)',
      'rgba(22, 30, 39, 0.72)'
    ),
    '--left-nav-surface-gradient': v(
      'linear-gradient(180deg, rgba(255, 255, 255, 0.76) 0%, rgba(255, 255, 255, 0.54) 100%)',
      'linear-gradient(180deg, rgba(28, 37, 48, 0.80) 0%, rgba(20, 27, 36, 0.62) 100%)'
    ),
    '--left-nav-border': v(
      'rgba(255, 255, 255, 0.72)',
      'rgba(255, 255, 255, 0.08)'
    ),
    '--left-nav-shadow': v(
      '1px 0 10px rgba(27, 38, 52, 0.045), inset -1px 0 0 rgba(255, 255, 255, 0.54)',
      '1px 0 10px rgba(0, 0, 0, 0.35), inset -1px 0 0 rgba(255, 255, 255, 0.05)'
    ),
    '--left-nav-sheen': v(
      'linear-gradient(112deg, rgba(255, 255, 255, 0.58) 0%, rgba(255, 255, 255, 0.2) 44%, rgba(255, 255, 255, 0.36) 100%)',
      'linear-gradient(112deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 44%, rgba(255, 255, 255, 0.05) 100%)'
    ),
    '--left-nav-sheen-shadow': v(
      'inset -1px 0 0 rgba(255, 255, 255, 0.76), inset 1px 0 0 rgba(255, 255, 255, 0.36)',
      'inset -1px 0 0 rgba(255, 255, 255, 0.08), inset 1px 0 0 rgba(255, 255, 255, 0.04)'
    ),
    '--left-nav-expanded-surface': v(
      'rgba(255, 255, 255, 0.18)',
      'rgba(22, 30, 39, 0.55)'
    ),
    '--left-nav-expanded-gradient': v(
      'linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.14) 100%)',
      'linear-gradient(180deg, rgba(28, 37, 48, 0.62) 0%, rgba(20, 27, 36, 0.46) 100%)'
    ),
    '--left-nav-expanded-blur': 'blur(14px) saturate(140%)',
    '--left-nav-expanded-border': v(
      'rgba(255, 255, 255, 0.62)',
      'rgba(255, 255, 255, 0.10)'
    ),
    '--left-nav-expanded-shadow': v(
      '4px 0 16px rgba(27, 38, 52, 0.075), inset -1px 0 0 rgba(255, 255, 255, 0.48)',
      '4px 0 16px rgba(0, 0, 0, 0.45), inset -1px 0 0 rgba(255, 255, 255, 0.06)'
    ),
    '--left-nav-item-color': v('#24323D', '#D4DAE2'),
    '--left-nav-item-fill': v('#24323D', '#D4DAE2'),
    '--left-nav-hover-bg': v(
      'rgba(36, 50, 61, 0.055)',
      'rgba(230, 235, 240, 0.07)'
    ),
    '--left-nav-hover-shadow': v(
      'inset 0 0 0 1px rgba(36, 50, 61, 0.045)',
      'inset 0 0 0 1px rgba(230, 235, 240, 0.05)'
    ),
    '--left-nav-active-bg': v(
      'linear-gradient(135deg, rgba(255, 255, 255, 0.84) 0%, rgba(255, 255, 255, 0.56) 100%)',
      'linear-gradient(135deg, rgba(11, 214, 141, 0.16) 0%, rgba(11, 214, 141, 0.07) 100%)'
    ),
    '--left-nav-active-shadow': v(
      '0 2px 6px rgba(34, 47, 62, 0.035), inset 0 0 0 1px rgba(11, 214, 141, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.72)',
      '0 2px 6px rgba(0, 0, 0, 0.25), inset 0 0 0 1px rgba(11, 214, 141, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.06)'
    ),
    '--left-nav-active-color': v('#0A7E5B', '#6EE7B7'),
    '--left-nav-active-text-color': v('#0A7E5B', '#6EE7B7'),
    '--left-nav-active-accent': '#0BD68D',
    '--left-nav-section-color': v(
      'rgba(36, 50, 61, 0.58)',
      'rgba(230, 235, 240, 0.55)'
    ),
    '--left-nav-subitem-color': v('#24323D', '#D4DAE2'),
    '--left-nav-subitem-muted-color': v(
      'rgba(36, 50, 61, 0.52)',
      'rgba(230, 235, 240, 0.50)'
    ),
    '--left-nav-subitem-hover-color': v('#24323D', '#E6EBF0'),
    '--left-nav-subitem-hover-bg': v(
      'rgba(36, 50, 61, 0.045)',
      'rgba(230, 235, 240, 0.06)'
    ),
    '--left-nav-subitem-hover-shadow': 'none',
    '--left-nav-subitem-active-bg': v(
      'linear-gradient(135deg, rgba(255, 255, 255, 0.84) 0%, rgba(255, 255, 255, 0.56) 100%)',
      'linear-gradient(135deg, rgba(11, 214, 141, 0.16) 0%, rgba(11, 214, 141, 0.07) 100%)'
    ),
    '--left-nav-subitem-active-shadow': v(
      '0 2px 6px rgba(34, 47, 62, 0.03), inset 0 0 0 1px rgba(11, 214, 141, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.7)',
      '0 2px 6px rgba(0, 0, 0, 0.22), inset 0 0 0 1px rgba(11, 214, 141, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
    ),
    '--left-nav-subitem-active-color': v('#0A7E5B', '#6EE7B7'),
    '--left-nav-subitem-active-accent': '#0BD68D',
    '--left-nav-subitem-divider-color': v(
      'rgba(36, 50, 61, 0.08)',
      'rgba(230, 235, 240, 0.10)'
    ),
    '--left-nav-subitem-skeleton-bg': v(
      'rgba(36, 50, 61, 0.08)',
      'rgba(230, 235, 240, 0.10)'
    ),
  }
}

export const getAppCssVars = (mode: AppearanceMode) => {
  const v = pick(mode)

  return {
    // ---- page canvas ----
    '--app-page-bg': PAGE_BG[mode],
    '--app-page-bg-image': v(
      `${NOISE}, radial-gradient(circle at 12% 14%, rgba(11, 214, 141, 0.115) 0, rgba(11, 214, 141, 0.075) 8%, rgba(11, 214, 141, 0) 20%), radial-gradient(circle at 86% 10%, rgba(34, 141, 255, 0.11) 0, rgba(34, 141, 255, 0.07) 9%, rgba(34, 141, 255, 0) 22%), radial-gradient(circle at 72% 88%, rgba(87, 78, 249, 0.075) 0, rgba(87, 78, 249, 0.05) 8%, rgba(87, 78, 249, 0) 20%), linear-gradient(180deg, #fbfbfd 0%, #f3f6fb 100%)`,
      `${NOISE}, radial-gradient(circle at 12% 14%, rgba(11, 214, 141, 0.16) 0, rgba(11, 214, 141, 0.09) 8%, rgba(11, 214, 141, 0) 20%), radial-gradient(circle at 86% 10%, rgba(34, 141, 255, 0.14) 0, rgba(34, 141, 255, 0.08) 9%, rgba(34, 141, 255, 0) 22%), radial-gradient(circle at 72% 88%, rgba(87, 78, 249, 0.12) 0, rgba(87, 78, 249, 0.07) 8%, rgba(87, 78, 249, 0) 20%), linear-gradient(180deg, #121923 0%, #0D1319 100%)`
    ),
    '--app-page-bg-blend': 'soft-light, normal, normal, normal, normal',
    '--app-page-bg-size': '140px 140px, auto, auto, auto, auto',

    // ---- ink / text ----
    '--app-ink': v('#24323D', '#E6EBF0'),
    '--app-ink-rgb': v('36, 50, 61', '230, 235, 240'),
    '--app-text-primary': v('#1A1A1A', '#E6EBF0'),
    '--app-text-secondary': v('#77778A', '#9AA4B2'),
    '--app-text-disabled': v(
      'rgba(47, 43, 61, 0.42)',
      'rgba(226, 232, 240, 0.42)'
    ),

    // ---- accent ----
    '--app-accent': '#0BD68D',
    '--app-accent-rgb': '11, 214, 141',
    '--app-accent-text': v('#0A7E5B', '#6EE7B7'),
    '--app-accent-soft': v(
      'rgba(11, 214, 141, 0.10)',
      'rgba(11, 214, 141, 0.16)'
    ),
    '--app-accent-soft-strong': v(
      'rgba(11, 214, 141, 0.22)',
      'rgba(11, 214, 141, 0.28)'
    ),
    '--app-active-border-strong': v(
      'rgba(11, 214, 141, 0.72)',
      'rgba(11, 214, 141, 0.60)'
    ),
    '--app-accent-glow': v(
      'radial-gradient(circle, rgba(11, 214, 141, 0.24) 0%, rgba(34, 141, 255, 0.12) 45%, rgba(34, 141, 255, 0) 72%)',
      'radial-gradient(circle, rgba(11, 214, 141, 0.30) 0%, rgba(34, 141, 255, 0.16) 45%, rgba(34, 141, 255, 0) 72%)'
    ),

    // ---- borders / muted fills ----
    '--app-border-faint': v(
      'rgba(36, 50, 61, 0.06)',
      'rgba(230, 235, 240, 0.07)'
    ),
    '--app-border-subtle': v(
      'rgba(36, 50, 61, 0.08)',
      'rgba(230, 235, 240, 0.10)'
    ),
    '--app-border-strong': v(
      'rgba(36, 50, 61, 0.12)',
      'rgba(230, 235, 240, 0.16)'
    ),
    '--app-surface-muted': v(
      'rgba(36, 50, 61, 0.035)',
      'rgba(255, 255, 255, 0.05)'
    ),
    '--app-card-surface': v(
      'rgba(255, 255, 255, 0.42)',
      'rgba(255, 255, 255, 0.04)'
    ),
    '--app-scrim': v('rgba(0, 0, 0, 0.20)', 'rgba(0, 0, 0, 0.55)'),
    '--app-bubble-self': v('#EEEDFD', 'rgba(124, 118, 255, 0.18)'),

    // ---- glass ----
    '--app-glass-surface': v(
      'rgba(255, 255, 255, 0.66)',
      'rgba(22, 30, 39, 0.62)'
    ),
    '--app-glass-surface-strong': v(
      'rgba(255, 255, 255, 0.78)',
      'rgba(22, 30, 39, 0.80)'
    ),
    '--app-glass-border': v(
      'rgba(255, 255, 255, 0.7)',
      'rgba(255, 255, 255, 0.10)'
    ),
    '--app-glass-shadow': v(
      '0 4px 12px rgba(34, 47, 62, 0.045)',
      '0 4px 12px rgba(0, 0, 0, 0.28)'
    ),
    '--app-glass-shadow-elevated': v(
      '0 8px 24px rgba(34, 47, 62, 0.10)',
      '0 8px 24px rgba(0, 0, 0, 0.45)'
    ),
    '--app-glass-inner-border': v(
      'inset 0 0 0 1px rgba(255, 255, 255, 0.44)',
      'inset 0 0 0 1px rgba(255, 255, 255, 0.05)'
    ),
    '--app-glass-highlight': v(
      'rgba(255, 255, 255, 0.72)',
      'rgba(255, 255, 255, 0.06)'
    ),
    '--app-glass-highlight-strong': v(
      'rgba(255, 255, 255, 0.96)',
      'rgba(255, 255, 255, 0.10)'
    ),
    '--app-glass-highlight-shadow':
      'inset 0 1px 0 var(--app-glass-highlight-strong), inset 0 -1px 0 var(--app-glass-highlight)',
    '--app-glass-blur': 'blur(18px) saturate(145%)',

    // ---- widgets ----
    '--app-widget-radius': '8px',
    '--app-widget-surface': v(
      'rgba(255, 255, 255, 0.72)',
      'rgba(22, 30, 39, 0.72)'
    ),
    '--app-widget-content-surface': v('#ffffff', '#161E27'),
    '--app-widget-header-surface': v(
      'rgba(255, 255, 255, 0.58)',
      'rgba(28, 37, 48, 0.72)'
    ),
    '--app-widget-header-hover': v(
      'rgba(255, 255, 255, 0.7)',
      'rgba(34, 45, 58, 0.80)'
    ),
    '--app-widget-shadow': v(
      '0 4px 12px rgba(34, 47, 62, 0.045)',
      '0 4px 12px rgba(0, 0, 0, 0.28)'
    ),
    '--app-scroll-sheet-surface': v(
      'rgba(255, 255, 255, 0.52)',
      'rgba(22, 30, 39, 0.58)'
    ),
    '--app-scroll-sheet-blur': 'blur(24px) saturate(165%)',
    '--app-widget-header-glass': v(
      'linear-gradient(180deg, rgba(255, 255, 255, 0.86) 0%, rgba(255, 255, 255, 0.78) 100%)',
      'linear-gradient(180deg, rgba(31, 41, 53, 0.90) 0%, rgba(26, 35, 45, 0.82) 100%)'
    ),
    '--app-widget-header-glass-hover': v(
      'linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%)',
      'linear-gradient(180deg, rgba(36, 47, 60, 0.94) 0%, rgba(30, 40, 51, 0.86) 100%)'
    ),
    '--app-widget-subheader-glass': v(
      'linear-gradient(180deg, rgba(255, 255, 255, 0.78) 0%, rgba(255, 255, 255, 0.68) 100%)',
      'linear-gradient(180deg, rgba(28, 37, 48, 0.82) 0%, rgba(24, 32, 42, 0.72) 100%)'
    ),
    '--app-widget-header-shadow': v(
      'inset 0 1px 0 rgba(255, 255, 255, 0.96), inset 0 -1px 0 rgba(255, 255, 255, 0.48), 0 2px 6px rgba(34, 47, 62, 0.035)',
      'inset 0 1px 0 rgba(255, 255, 255, 0.10), inset 0 -1px 0 rgba(255, 255, 255, 0.04), 0 2px 6px rgba(0, 0, 0, 0.25)'
    ),
    '--app-widget-subheader-shadow': v(
      'inset 0 1px 0 rgba(255, 255, 255, 0.78), inset 0 -1px 0 rgba(255, 255, 255, 0.34), 0 1px 4px rgba(34, 47, 62, 0.032)',
      'inset 0 1px 0 rgba(255, 255, 255, 0.07), inset 0 -1px 0 rgba(255, 255, 255, 0.03), 0 1px 4px rgba(0, 0, 0, 0.22)'
    ),
    '--app-table-head-surface': v(
      'rgba(255, 255, 255, 0.5)',
      'rgba(255, 255, 255, 0.04)'
    ),
    '--app-input-surface': v(
      'rgba(255, 255, 255, 0.58)',
      'rgba(255, 255, 255, 0.05)'
    ),
    '--app-button-surface': v(
      'rgba(255, 255, 255, 0.46)',
      'rgba(255, 255, 255, 0.05)'
    ),
    '--app-button-border': v(
      'rgba(255, 255, 255, 0.7)',
      'rgba(255, 255, 255, 0.10)'
    ),
    '--app-popover-surface': v(
      'rgba(255, 255, 255, 0.78)',
      'rgba(26, 35, 45, 0.94)'
    ),
    '--app-popover-blur': 'blur(18px) saturate(145%)',

    // ---- hover / active ----
    '--app-hover-bg': v('rgba(36, 50, 61, 0.055)', 'rgba(230, 235, 240, 0.06)'),
    '--app-hover-border': 'transparent',
    '--app-hover-shadow': 'none',
    '--app-hover-color': v('#24323D', '#E6EBF0'),
    '--app-active-bg': v(
      'linear-gradient(135deg, rgba(255, 255, 255, 0.84) 0%, rgba(255, 255, 255, 0.56) 100%)',
      'linear-gradient(135deg, rgba(11, 214, 141, 0.14) 0%, rgba(11, 214, 141, 0.06) 100%)'
    ),
    '--app-active-border': v(
      'rgba(11, 214, 141, 0.24)',
      'rgba(11, 214, 141, 0.32)'
    ),
    '--app-active-shadow': v(
      '0 2px 6px rgba(34, 47, 62, 0.035), inset 0 0 0 1px rgba(11, 214, 141, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.72)',
      '0 2px 6px rgba(0, 0, 0, 0.24), inset 0 0 0 1px rgba(11, 214, 141, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.06)'
    ),
    '--app-active-color': 'var(--app-accent-text)',
    '--app-active-accent': 'var(--app-accent)',

    // ---- menu aliases ----
    '--app-menu-hover-bg': 'var(--app-hover-bg)',
    '--app-menu-hover-border': 'var(--app-hover-border)',
    '--app-menu-hover-shadow': 'var(--app-hover-shadow)',
    '--app-menu-active-bg': 'var(--app-active-bg)',
    '--app-menu-active-border': 'var(--app-active-border)',
    '--app-menu-active-shadow': 'var(--app-active-shadow)',
    '--app-menu-active-color': 'var(--app-active-color)',

    // ---- avatar ----
    '--app-avatar-bg': v(
      'linear-gradient(180deg, rgba(255, 255, 255, 0.82) 0%, rgba(246, 250, 249, 0.68) 100%)',
      'linear-gradient(180deg, rgba(44, 54, 67, 0.92) 0%, rgba(34, 43, 54, 0.88) 100%)'
    ),
    '--app-avatar-color': v('#32423E', '#D4DAE2'),
    '--app-avatar-border': v(
      'rgba(11, 214, 141, 0.18)',
      'rgba(11, 214, 141, 0.22)'
    ),
    '--app-avatar-shadow': v(
      'inset 0 0 0 1px rgba(255, 255, 255, 0.72), 0 1px 3px rgba(34, 47, 62, 0.035)',
      'inset 0 0 0 1px rgba(255, 255, 255, 0.06), 0 1px 3px rgba(0, 0, 0, 0.30)'
    ),
    '--app-avatar-blur': 'none',

    // ---- chrome ----
    '--app-scrollbar-thumb': v('#ebebeb', '#2F3A48'),
    '--app-scrollbar-thumb-border': v('#e2e2e2', '#3C4654'),
    '--app-code-color': v('#d400ff', '#E879F9'),

    ...getLeftNavCssVars(mode),
  }
}

export type AppCssVars = ReturnType<typeof getAppCssVars>
